import { Component, Input, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import {
    EndPoint,
    ScanHistory,
    Subdomain,
    Vulnerability,
    VulnerabilitySeverity,
    VulnerabilitySeverityMap,
} from '../../../interfaces/asm/models';
import { SubdomainsComponent } from './subdomains/subdomains.component';
import { EndpointsComponent } from './endpoints/endpoints.component';
import { VulnerabilitiesComponent } from './vulnerabilities/vulnerabilities.component';
import { IpAddressesComponent } from './ip-addresses/ip-addresses.component';
import { PortsComponent } from './ports/ports.component';
import { TechnologiesComponent } from './technologies/technologies.component';
import { HttpStatusComponent } from './http-status/http-status.component';
import { OsintComponent } from './osint/osint.component';
import { TargetInformationComponent } from './target-information/target-information.component';
import { VulnerabilityBreakdownComponent } from './vulnerability-breakdown/vulnerability-breakdown.component';
import { VulnerabilityHighlightsComponent } from './vulnerability-highlights/vulnerability-highlights.component';

@Component({
    selector: 'app-home',
    standalone: true,
    imports: [
        MatCardModule,
        SubdomainsComponent,
        EndpointsComponent,
        VulnerabilitiesComponent,
        OsintComponent,
        TargetInformationComponent,
        HttpStatusComponent,
        VulnerabilityBreakdownComponent,
        VulnerabilityHighlightsComponent,
        IpAddressesComponent,
        PortsComponent,
        TechnologiesComponent,
    ],
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
})
export class HomeComponent {
    @Input() scan: ScanHistory;
    @Input() listSubdomains: Subdomain[];
    @Input() listEndpoints: EndPoint[];
    @Input() listVulnerabilities: Vulnerability[];

    @ViewChild('subdomains', { static: false }) subdomains: SubdomainsComponent;
    @ViewChild('endpoints', { static: false }) endpoints: EndpointsComponent;
    @ViewChild('vulnerabilities', { static: false })
    vulnerabilities: VulnerabilitiesComponent;
    @ViewChild('osint', { static: false }) osint: OsintComponent;
    @ViewChild('targetInformation', { static: false })
    targetInformation: TargetInformationComponent;
    @ViewChild('httpStatusBreakdown', { static: false })
    httpStatusBreakdown: HttpStatusComponent;
    @ViewChild('vulnerabilityBreakdown', { static: false })
    vulnerabilityBreakdown: VulnerabilityBreakdownComponent;
    @ViewChild('vulnerabilityHighlights', { static: false })
    vulnerabilityHighlights: VulnerabilityHighlightsComponent;
    @ViewChild('ipAddresses', { static: false })
    ipAddresses: IpAddressesComponent;
    @ViewChild('ports', { static: false }) ports: PortsComponent;
    @ViewChild('technologies', { static: false })
    technologies: TechnologiesComponent;

    constructor() {}

    // changeDataScan
    changeDataScan() {
        if (this.scan) {
            this.subdomains.subdomainCount = this.scan.subdomain_count
                ? this.scan.subdomain_count
                : 0;
            this.endpoints.endpointCount = this.scan.endpoint_count
                ? this.scan.endpoint_count
                : 0;
            this.vulnerabilities.scan = this.scan;
            this.vulnerabilities.vulnerabilityCount = this.scan
                .vulnerability_count
                ? this.scan.vulnerability_count
                : 0;
            this.osint.scan = this.scan;
            this.osint.changeData();
            this.targetInformation.scan = this.scan;
            this.targetInformation.changeData();
            this.ipAddresses.scan = this.scan;
            this.ipAddresses.changeData();
            this.ports.scan = this.scan;
            this.ports.changeData();
            this.technologies.scan = this.scan;
            this.technologies.changeData();
        }
    }

    // changeDataSubdomains
    changeDataSubdomains() {
        if (this.listSubdomains) {
            let aliveSubdomains: string[] = [];
            this.listSubdomains
                .filter((subdomain) => subdomain.http_status == 200)
                .forEach((subdomain) => {
                    aliveSubdomains.push(subdomain.name.toString());
                });
            this.subdomains.aliveCount =
                this.getDistinctStrings(aliveSubdomains).length;

            let labels: string[] = [];
            let series: number[] = [];
            let colors: string[] = [];
            let data = this.listSubdomains
                .filter((subdomain) => subdomain.http_status != 0)
                .sort((a, b) => a.http_status - b.http_status);
            data.forEach((subdomain) => {
                const result = labels.length
                    ? labels.indexOf(subdomain.http_status.toString())
                    : -1;
                if (result != -1) {
                    series[result] += 1;
                } else {
                    labels.push(subdomain.http_status.toString());
                    series.push(1);
                    if (
                        subdomain.http_status >= 200 &&
                        subdomain.http_status < 300
                    ) {
                        colors.push('#4CAF50');
                    } else if (
                        subdomain.http_status >= 300 &&
                        subdomain.http_status < 400
                    ) {
                        colors.push('#FFC107');
                    } else {
                        colors.push('#F44336');
                    }
                }
            });
            this.httpStatusBreakdown.data.labels = labels;
            this.httpStatusBreakdown.data.series = series;
            this.httpStatusBreakdown.data.colors = colors;
            this.httpStatusBreakdown.changeData();
        }
    }

    // changeDataEndPoints
    changeDataEndPoints() {
        if (this.listEndpoints) {
            let aliveEndpoints: string[] = [];
            this.listEndpoints
                .filter((endpoint) => endpoint.http_status == 200)
                .forEach((endpoint) => {
                    aliveEndpoints.push(endpoint.http_url);
                });
            this.endpoints.aliveCount =
                this.getDistinctStrings(aliveEndpoints).length;
        }
    }

    // changeDataVulnerabilities
    changeDataVulnerabilities() {
        if (this.listVulnerabilities) {
            this.vulnerabilityHighlights.data = this.listVulnerabilities;
            this.vulnerabilityHighlights.changeData();
            this.listVulnerabilities.forEach(element => {
                switch (element.severity.toString()) {
                    case 'Info':
                        this.scan.info_vulnerability_count = (this.scan.info_vulnerability_count ? this.scan.info_vulnerability_count : 0) + 1;
                        break;

                    case 'Low':
                        this.scan.low_vulnerability_count = (this.scan.low_vulnerability_count ? this.scan.low_vulnerability_count : 0) + 1;
                        break;

                    case 'Medium':
                        this.scan.medium_vulnerability_count = (this.scan.medium_vulnerability_count ? this.scan.medium_vulnerability_count : 0) + 1;
                        break;

                    case 'High':
                        this.scan.high_vulnerability_count = (this.scan.high_vulnerability_count ? this.scan.high_vulnerability_count : 0) + 1;
                        break;

                    case 'Critical':
                        this.scan.critical_vulnerability_count = (this.scan.critical_vulnerability_count ? this.scan.critical_vulnerability_count : 0) + 1;
                        break;

                    case 'Unknown':
                        this.scan.unknown_vulnerability_count = (this.scan.unknown_vulnerability_count ? this.scan.unknown_vulnerability_count : 0) + 1;
                        break;

                    default:
                        break;
                }
            });

            this.scan.critical_vulnerability_count = (this.scan.critical_vulnerability_count ? this.scan.critical_vulnerability_count : 0);
            this.scan.high_vulnerability_count = (this.scan.high_vulnerability_count ? this.scan.high_vulnerability_count : 0);
            this.scan.medium_vulnerability_count = (this.scan.medium_vulnerability_count ? this.scan.medium_vulnerability_count : 0);
            this.scan.low_vulnerability_count = (this.scan.low_vulnerability_count ? this.scan.low_vulnerability_count : 0);
            this.scan.info_vulnerability_count = (this.scan.info_vulnerability_count ? this.scan.info_vulnerability_count : 0);
            this.scan.unknown_vulnerability_count = (this.scan.unknown_vulnerability_count ? this.scan.unknown_vulnerability_count : 0);

            this.vulnerabilityBreakdown.data = [
                this.scan.critical_vulnerability_count,
                this.scan.high_vulnerability_count,
                this.scan.medium_vulnerability_count,
                this.scan.low_vulnerability_count,
                this.scan.info_vulnerability_count,
                this.scan.unknown_vulnerability_count
            ];

            this.vulnerabilityBreakdown.changeData();
        }
    }

    // getDistinctStrings
    getDistinctStrings(arr: string[]): string[] {
        return [...new Set(arr)];
    }
}
